import clsx from "clsx";
import { Link } from "react-router-dom";

import type { Crumb } from "../api/types";

/** Where a crumb leads: an item has its page, any other element has the element page. */
export function crumbPath(crumb: Crumb): string | null {
  if (!crumb.element_id) return null;
  const id = encodeURIComponent(crumb.element_id);
  return crumb.level === "item" || crumb.level === "main_parent" ? `/items/${id}` : `/elements/${id}`;
}

interface BreadcrumbProps {
  crumbs: readonly Crumb[];
  /** Replaces the default places the crumbs lead to. Return null for a crumb that is not a link. */
  linkFor?: (crumb: Crumb) => string | null;
  className?: string;
}

/**
 * The family breadcrumb: library, family, sub-family, main parent, item. The last crumb is where
 * the person is, so it is not a link.
 */
export function Breadcrumb({ crumbs, linkFor = crumbPath, className }: BreadcrumbProps) {
  if (crumbs.length === 0) return null;
  const last = crumbs.length - 1;
  return (
    <nav aria-label="Where it sits" className={clsx("breadcrumb", className)}>
      <ol>
        {crumbs.map((crumb, index) => {
          const to = index === last ? null : linkFor(crumb);
          return (
            <li key={`${crumb.level}:${crumb.element_id ?? crumb.label}:${index}`} data-level={crumb.level}>
              {to ? (
                <Link to={to}>{crumb.label}</Link>
              ) : (
                <span aria-current={index === last ? "page" : undefined}>{crumb.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
